<template>
  <Teleport to="body">
    <Transition name="modal" appear>
      <div v-if="open" class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="$emit('cancel')" @keydown.esc="$emit('cancel')">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-none"></div>
        <form role="dialog" aria-modal="true" :aria-label="heading" @submit.prevent="submit"
          class="relative w-full max-w-md bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700">
          <div class="px-5 pt-5 pb-4">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ heading }}</h3>
            <p v-if="mode === 'create'" class="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
              {{ hasDefault ? 'It starts as a copy of your default resume, so you do not retype your history.' : 'This will be your default resume. New ones start from it.' }}
            </p>
          </div>

          <div class="px-5 pb-5 space-y-4">
            <div>
              <label for="resume-name" class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Resume title</label>
              <input id="resume-name" ref="nameInput" v-model="name" type="text" maxlength="120" autocomplete="off" placeholder="For example, Product Designer"
                class="w-full h-9 px-3 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400">
            </div>

            <fieldset>
              <legend class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Template</legend>
              <div class="max-h-64 overflow-y-auto -mx-1 px-1 py-1"><TemplatePicker v-model="template" columns="grid-cols-3 sm:grid-cols-4" /></div>
            </fieldset>

            <p v-if="error" role="alert" class="text-[13px] text-red-600 dark:text-red-400">{{ error }}</p>
          </div>

          <div class="flex justify-end gap-2 px-5 py-3.5 border-t border-gray-100 dark:border-gray-700">
            <button type="button" @click="$emit('cancel')" :disabled="saving"
              class="h-9 px-4 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-60">Cancel</button>
            <button type="submit" :disabled="saving || !name.trim()"
              class="inline-flex items-center gap-1.5 h-9 px-4 text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed">
              <svg v-if="saving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"/></svg>
              {{ mode === 'create' ? 'Create resume' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import TemplatePicker from './TemplatePicker.vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  mode: { type: String, default: 'create' }, // create | edit
  resume: { type: Object, default: null }, // the one being edited
  hasDefault: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  error: { type: String, default: '' },
})
const emit = defineEmits(['submit', 'cancel'])

const name = ref('')
const template = ref('modern')
const nameInput = ref(null)

const heading = computed(() => (props.mode === 'create' ? 'New resume' : 'Resume details'))

// Each time it opens it starts from the resume being edited, or empty for a new one.
watch(() => props.open, (isOpen) => {
  if (!isOpen) return
  name.value = props.mode === 'edit' ? props.resume?.name || '' : ''
  template.value = props.mode === 'edit' ? props.resume?.template || 'modern' : 'modern'
  nextTick(() => nameInput.value?.focus())
})

function submit() {
  if (!name.value.trim() || props.saving) return
  emit('submit', { name: name.value.trim(), template: template.value })
}
</script>
